import { getDupes } from "./array"
import { isNested, validate } from "../Schema/Schema"
import { JSONSchema7, JSONSchema7Definition } from "json-schema"
import { CSVItemError } from "../Errors/CSVItemError"
import { CSVError } from "../Errors/CSVError"
import { SchemaItemError } from "../Errors/SchemaItemError"
import { SchemaError } from "../Errors/SchemaError"
import { SchemaValidationItemError } from "../Errors/SchemaValidationItemError"
import { SchemaValidationError } from "../Errors/SchemaValidationError"
import type { CSVParseParam } from "csvtojson/v2/Parameters"
import { ConvertFn } from "../../Components/Common/Render/Variants/JobInput/Common/CSV/CSV"
import get from "lodash/get"
import { flatten as flatJSON } from "flat"
import { clean } from "./object"
import { InputAndMetadata } from "../../Models/Client/JobSubmitData"
import { isJSONSchema7 } from "universal-schema/lib"
import { utils } from "@rjsf/core"

if (process.env.NODE_ENV !== "production") {
    // This module is being used from both web worker and the main thread, which seems causing a conflict in webpack's hot reloading mechanism.
    // This is a quick workaround taken from https://github.com/pmmmwh/react-refresh-webpack-plugin/issues/176#issuecomment-683150213
    ;(global as any).$RefreshReg$ = () => {}
    ;(global as any).$RefreshSig$ = () => () => {}
}

export type JSONPrim = string | number | boolean | null

export type JSONArr = JSONPrim[] | JSONObj[]

export interface JSONObj {
    [index: string]: JSONPrim | JSONArr | JSONObj
}

export type JSONData = JSONArr | JSONObj

export type ParseFn = (item: string) => any

/**
 * Function takes json data and returns a flattened version in
 * csv format. Flattened nested objects and arrays are transformed
 * into columns with . and int format e.g.
 *
 * `some.parent.0.child.obj`
 *
 * ```
 * {
 *      some: {
 *          parent: [
 *              {
 *                  child: {
 *                      obj: "primative"
 *                  }
 *              }
 *          ]
 *      }
 * }
 * ```
 *
 * @param json Any json object or array
 * @param delimiter some value to delimit by e.g. "," ";" "\t"
 */
export async function jsonconvert({
    delimiter = ",",
    json
}: {
    json: JSONData
    delimiter?: string
}): Promise<string> {
    const { transforms, Parser } = await import("json2csv").then(
        lib => lib.default
    )
    const { flatten } = transforms

    const opts: json2csv.Options<unknown> = {
        transforms: [flatten({ arrays: true, objects: true, separator: "." })],
        includeEmptyRows: true,
        delimiter
    }

    const parser = new Parser(opts)
    return parser.parse(json)
}

/**
 * Converts a csv string into it's representative nested json.
 * Arrays and nesting follow a . and int format, e.g.
 *
 * `some.parent.0.child.obj`
 *
 * ```
 * {
 *      some: {
 *          parent: [
 *              {
 *                  child: {
 *                      obj: "primative"
 *                  }
 *              }
 *          ]
 *      }
 * }
 * ```
 *
 * This function will also validate the data if provided a validationHeader
 * arg that is an array of strings which represent expected headers. If a
 * header is duplicated in the csv, this will error as will missing headers.
 *
 * @param csv string of csv to convert
 * @param delimiter some delimiter(s) to check for e.g. "," ";" "\t"
 * @param expectedColumns header item array to check against csv
 */
export async function csvconvert({
    csv,
    delimiter = ",",
    expectedColumns = [],
    parser,
    headers
}: {
    csv: string
    delimiter?: string | string[]
    expectedColumns?: string[]
    parser?: {
        [key: string]: string | ParseFn
    }
    headers?: string[]
}): Promise<JSONArr> {
    const csv2json = await import("csvtojson").then(lib => lib.default)

    return new Promise((resolve, reject) => {
        const options: Partial<CSVParseParam> = {
            nullObject: true,
            delimiter,
            noheader: false
        }

        if (parser !== undefined) options.colParser = parser
        if (headers !== undefined) options.headers = headers
        const fn = csv2json(options)
        const errors: CSVItemError[] = []

        fn.on("error", error => reject(error))
        fn.on("header", (header: string[]) => {
            Object.entries(getDupes(header)).forEach(([value, columns]) => {
                columns.forEach(column => {
                    errors.push(
                        new CSVItemError("Duplicate column", column, -1, value)
                    )
                })
            })

            expectedColumns
                .reduce<string[]>((m, h) => {
                    if (!header.includes(h)) {
                        m.push(h)
                    }
                    return m
                }, [])
                .forEach(missing => {
                    errors.push(
                        new CSVItemError("Missing column", -1, -1, missing)
                    )
                })
        })

        fn.fromString(csv).then(json => {
            if (errors.length) {
                reject(new CSVError(errors))
            }

            resolve(json)
        })
    })
}

function columnParser(
    columns: [string, JSONSchema7Definition | undefined][],
    rootSchema: JSONSchema7
) {
    function toParser(schema: JSONSchema7Definition): string | ParseFn {
        if (!isJSONSchema7(schema)) {
            return "string"
        }

        if (schema.$ref !== undefined) {
            return toParser(utils.resolveSchema(schema, rootSchema))
        }

        if (schema.allOf !== undefined && schema.allOf.length > 0) {
            return toParser(schema.allOf[0])
        }

        switch (schema.type) {
            case "integer":
            case "number":
                return "number"
            case "boolean":
                return item => item === "true"
            default:
                return "string"
        }
    }

    return columns.reduce<{
        [column: string]: string | ParseFn
    }>((obj, [prop, schema]) => {
        obj[prop] = schema === undefined ? "string" : toParser(schema)
        console.log(obj[prop])
        return obj
    }, {})
}

export function csvConvertValidator(validationSchema: JSONSchema7): {
    convertFn: ConvertFn
    columns: string[]
} {
    if (validationSchema.type !== "object") {
        throw new SchemaError([
            new SchemaItemError("Validation schema base type must be an object")
        ])
    }

    if (validationSchema.properties === undefined) {
        throw new SchemaError([
            new SchemaItemError("Validation schema missing properties.")
        ])
    }

    if (isNested(validationSchema)) {
        throw new SchemaError([
            new SchemaItemError("Nested schema is not supported.")
        ])
    }

    const parser = columnParser(
        Object.entries(validationSchema.properties),
        validationSchema
    )
    const expectedColumns = Object.keys(validationSchema.properties)

    const convertFn = ({
        csv,
        delimiter = ",",
        headers,
        maxReportErrorRows
    }: {
        csv: string
        delimiter?: string | string[]
        headers?: string[]
        maxReportErrorRows: number
    }): Promise<InputAndMetadata[]> =>
        csvconvert({
            csv,
            delimiter,
            expectedColumns,
            parser,
            headers
        }).then(json =>
            toJobInputAndMetadataItems(
                json,
                validationSchema,
                (field: string) => !expectedColumns.includes(field),
                maxReportErrorRows
            )
        )

    return {
        convertFn,
        columns: expectedColumns
    }
}

export function toJobInputAndMetadataItems(
    items: JSONArr,
    schema: JSONSchema7,
    isMetadataField: (field: string) => boolean,
    maxReportErrorItemCount: number
): InputAndMetadata[] {
    const errors: SchemaValidationItemError[] = []
    let errorItemCount = 0

    for (let i = 0; i < items.length; i++) {
        const item = items[i]

        if (JSON.stringify(item).length > 250 * 1000) {
            errors.push(new SchemaValidationItemError("", "", i, "too_big"))
            errorItemCount += 1
        } else {
            const validationResult = validate(schema, item)

            errors.push(
                ...validationResult.errors.map(
                    ({ message, dataPath, keyword }) => {
                        const dataPathMod = dataPath.replace(/^\./, "")

                        return new SchemaValidationItemError(
                            message ?? "",
                            dataPathMod,
                            i,
                            keyword === "required" ? "missing" : "other",
                            keyword === "required"
                                ? undefined
                                : `${get(item, dataPathMod)}`
                        )
                    }
                )
            )

            errorItemCount += validationResult.errors.length > 0 ? 1 : 0
        }

        if (errorItemCount >= maxReportErrorItemCount) {
            break
        }
    }

    if (errorItemCount > 0) {
        throw new SchemaValidationError(
            errors,
            items.length,
            errorItemCount >= maxReportErrorItemCount
        )
    }

    const outputData: InputAndMetadata[] = []

    items.forEach((item: JSONObj | JSONPrim, index: number) => {
        if (!item) return
        Object.entries(item).reduce((obj, [k, v]) => {
            if (isMetadataField(k)) {
                if (k === "__superai__remove__") return obj
                if (!obj[index])
                    obj[index] = {
                        input: {},
                        metadata: {}
                    }
                ;(obj[index].metadata as JSONObj)[k] = v
            } else {
                if (!obj[index])
                    obj[index] = {
                        input: {},
                        metadata: {}
                    }
                ;(obj[index].input as JSONObj)[k] = v
            }
            return obj
        }, outputData)
    })

    return outputData
}

/**
 * Function takes a json file and extracts headers. This is to be
 * used in conjunction with the csv convert function in order to
 * validate the existence of multiple or missing headers from provided
 * csv data.
 *
 * @param json Sample data to be used as a basis for generated csv header
 */
export async function getCsvHeadersFromJSON(json: JSONData): Promise<string[]> {
    const [csv2json, { transforms, Parser }] = await Promise.all([
        import("csvtojson").then(lib => lib.default),
        import("json2csv").then(lib => lib.default)
    ])

    return new Promise((resolve, reject) => {
        const { flatten } = transforms

        const opts: json2csv.Options<unknown> = {
            transforms: [
                flatten({ arrays: true, objects: true, separator: "." })
            ],
            includeEmptyRows: true
        }

        const fn = csv2json({ flatKeys: true })
        fn.on("error", error => reject(error))
        fn.on("header", (header: string[]) => {
            resolve(header)
        })

        try {
            const parser = new Parser(opts)
            const csv = parser.parse(json)
            fn.fromString(csv)
        } catch (err) {
            reject(err)
        }
    })
}

export async function getHeaderListFromCSV({
    csv,
    delimiter = ","
}: {
    csv: string
    delimiter?: string | string[]
}): Promise<string[]> {
    const csv2json = await import("csvtojson").then(lib => lib.default)

    return new Promise((resolve, reject) => {
        const options: Partial<CSVParseParam> = {
            nullObject: true,
            ignoreEmpty: true,
            delimiter
        }

        const fn = csv2json(options)

        fn.on("error", error => reject(error))
        fn.on("header", resolve)

        fn.fromString(csv)
    })
}

export function transformHeaderRow(
    csvColumns: string[],
    expectedColumns: string[]
) {
    const info =
        csvColumns.find(item => item === "id_superai") !== undefined
            ? "The column 'id_superai' will be ignored. If you want to keep it, please rename it."
            : undefined
    const hasNestedInput =
        csvColumns.find(item => item.startsWith("input.")) !== undefined
    const metaCols: { [index: string]: number[] } = {}
    const columns = csvColumns.reduce<string[]>((cols, column, index) => {
        const col = hasNestedInput
            ? column.startsWith("input.")
                ? column.slice(6)
                : column.startsWith("output.") || column === "id_superai"
                ? `__superai__remove__.${column}`
                : column
            : column === ""
            ? "other_column"
            : column
        if (expectedColumns.includes(col)) {
            cols[index] = col
        } else if (metaCols[col] && metaCols[col].length > 0) {
            if (metaCols[col].length === 1) {
                cols[metaCols[col][0]] = `${cols[metaCols[col][0]]}.0`
            }

            cols[index] = `${col}.${metaCols[col].length}`
            metaCols[col].push(0)
        } else {
            metaCols[col] = [index]
            cols[index] = col
        }
        return cols
    }, [])
    return { columns, info }
}

export function flattenJSONObj(data: any): { [index: string]: any } {
    return flatJSON(clean(data))
}

export function flatJSONEntries(data: any) {
    return Object.entries(flattenJSONObj(data))
}

type Primitive = string | number | boolean | null

interface Column {
    name: string
    key: string | undefined
}

export function JSONArrayToFlatEntries(data: any[]): {
    array: [string, Primitive][][]
    keys: string[]
} {
    const output: {
        array: [string, Primitive][][]
        keys: string[]
    } = {
        array: [],
        keys: []
    }

    return data.reduce((items, obj) => {
        items.array.push(flatJSONEntries(obj))
        items.array[items.array.length - 1].forEach(([k]: [string]) => {
            if (!items.keys.includes(k)) items.keys.push(k)
        })
        return items
    }, output)
}

export function flatJobsCSV(
    jobs: any[],
    withFeedback: boolean = false
): string {
    if (!Array.isArray(jobs) || jobs.length === 0) return ""

    const { array, keys } = JSONArrayToFlatEntries(jobs)

    const columns: Column[] = [
        { name: "id", pattern: "metadata.id" },
        { name: "id_superai", pattern: "id" },
        { name: "status" },
        {
            name: "input",
            pattern: (key: string) =>
                /^input(?:\..+)$/.test(key) ? key : undefined
        },
        {
            name: "output",
            pattern: (key: string) =>
                /^output(?:\..+)$/.test(key) ? key : undefined
        },
        { name: "correct" },
        { name: withFeedback ? "messages" : undefined },
        { name: "tags" },
        { name: "created" },
        { name: "completed" },
        { name: "jobType" },
        { name: "uuid" },
        { name: "appId" },
        { name: "callbackUrl" },
        {
            pattern: (key: string) =>
                key === "metadata.id"
                    ? undefined
                    : key.match(/^metadata(?:\.(.+))$/)?.[1] ?? undefined
        }
    ].flatMap<Column>(({ name, pattern }) => {
        const matchedColumns = keys
            .flatMap<Column>(key => {
                if (typeof pattern === "string") {
                    return key === pattern && name !== undefined
                        ? [{ name, key }]
                        : []
                }

                if (pattern === undefined) {
                    return name === undefined || name !== key
                        ? []
                        : [{ name, key }]
                }

                const matched = pattern(key)

                return matched === undefined ? [] : [{ name: matched, key }]
            })
            .sort((one, another) => one.name.localeCompare(another.name))

        return matchedColumns.length > 0
            ? matchedColumns
            : name === undefined
            ? []
            : [{ name, key: undefined }]
    })

    return [columns.map(column => column.name as Primitive)]
        .concat(
            array.map(row =>
                columns.map(
                    column => row.find(([key]) => key === column.key)?.[1] ?? ""
                )
            )
        )
        .map(row =>
            row
                .map(
                    column =>
                        `"${
                            typeof column === "string"
                                ? column.replace(/"/g, '""')
                                : column || ""
                        }"`
                )
                .join(",")
        )
        .join("\n")
}
